/**
 * Recent searches MRU cache backed by localStorage (P74.3).
 *
 * Stored under `marsad:recent_searches` as a JSON array of
 * strings, most-recent-first, capped at MAX_RECENTS. Shared
 * across tabs of the same browser; never sent to the server
 * (privacy + offline-safe).
 *
 * Operators see the panel when the topnav input is focused +
 * empty, with at least one stored recent. Clicking one pre-fills
 * the input + opens the typeahead. A small "Clear" link wipes
 * the key.
 */

const STORAGE_KEY = "marsad:recent_searches";
export const MAX_RECENTS = 5;

function safeParse(raw: string | null): string[] {
  if (!raw) return [];
  try {
    const v = JSON.parse(raw);
    if (!Array.isArray(v)) return [];
    return v.filter((s): s is string => typeof s === "string");
  } catch {
    return [];
  }
}

export function readRecentSearches(): string[] {
  if (typeof window === "undefined" || !window.localStorage) {
    return [];
  }
  try {
    return safeParse(window.localStorage.getItem(STORAGE_KEY));
  } catch {
    return [];
  }
}

/**
 * Pure ranking pass: take the current MRU list + push `query` to
 * the front, deduping case-insensitively, capped at MAX_RECENTS.
 * Empty / whitespace-only queries are ignored (return the list
 * unchanged). Exposed for testing the pure pass without
 * localStorage.
 */
export function pushPure(current: string[], query: string): string[] {
  const trimmed = query.trim();
  if (!trimmed) return current;
  const norm = trimmed.toLowerCase();
  const filtered = current.filter((q) => q.toLowerCase() !== norm);
  const next = [trimmed, ...filtered];
  return next.slice(0, MAX_RECENTS);
}

export function pushRecentSearch(query: string): void {
  if (typeof window === "undefined" || !window.localStorage) {
    return;
  }
  const next = pushPure(readRecentSearches(), query);
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(next));
  } catch {
    /* quota / serialization error -- ignore */
  }
}

export function clearRecentSearches(): void {
  if (typeof window === "undefined" || !window.localStorage) {
    return;
  }
  try {
    window.localStorage.removeItem(STORAGE_KEY);
  } catch {
    /* ignore */
  }
}
